import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type {
  IGetReportMetadata,
  IKeyValuePair,
  IReport,
  IReportFilter,
  IReportMetadata,
  IReportParameter,
  ITableResult,
} from '@/api/types';
import type { StandardRequest } from '@/store';

import { v3Get, v3Post, v3PostWithResponseInterceptor } from './axios.instance.V3';
import { StringDecoder } from 'string_decoder';
import { Buffer } from 'buffer';
import { IExportClassUsersParam } from '@/api/types';

const url = {
  getReports: (clientID: number) => `/report/get/reports?clientId=${clientID}`,
  exportClassUsers: (
    clientID: number,
    isRemoved: number,
    includeStatus: boolean,
    includeExternalId: boolean,
    isBackgroundCheckRequired?: boolean,
  ) => {
    let url = `/SU/class/user/list/base64?IncludeStatus=${includeStatus}&ClassId=${clientID}&IsRemoved=${isRemoved}&IncludeExternalId=${includeExternalId}`;
    if (isBackgroundCheckRequired !== undefined) url += `&IsBackgroundCheckRequired=${isBackgroundCheckRequired}`;
    return url;
  },
  getReportMetadata: (reportId: number) => `/report/get/metadata?reportId=${reportId}`,
  getReportFilter: (reportId: number) => `/report/get/filters?reportId=${reportId}`,
  getTableResult: (reportId: number, csv?: boolean) => {
    let url = `/report/get/tableresults?reportId=${reportId}`;
    if (csv === true) url += '&csv=true';

    return url;
  },
} as const;

const getReports: StandardRequest<IReport[], number> = (clientID, config) => v3Get(url.getReports(clientID), config);

const getReportMetadata = (
  payload: IGetReportMetadata,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<IReportMetadata>> => v3Get(url.getReportMetadata(payload.reportId), config);

const getExportClassUsersResult = (
  payload: IExportClassUsersParam,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<string>> =>
  v3Get(
    url.exportClassUsers(
      payload.classId,
      payload.isRemoved,
      payload.includeStatus,
      payload.includeExternalId,
      payload.isBackgroundCheckRequired,
    ),
    config,
  );

const getReportFilter = (
  reportId: number,
  payload: IKeyValuePair[],
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<IReportFilter[]>> => v3Post(url.getReportFilter(reportId), payload, config);

const getTableResult = (
  reportId: number,
  payload: IReportParameter[],
  csv?: boolean,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<ITableResult>> => {
  if (csv)
    return v3PostWithResponseInterceptor(url.getTableResult(reportId, csv), responseInterceptor, payload, {
      ...config,
      responseType: 'arraybuffer',
    });
  return v3Post(url.getTableResult(reportId, csv), payload, config);
};

const responseInterceptor = (
  response: AxiosResponse<any>,
): AxiosResponse<unknown> | Promise<AxiosResponse<unknown>> => {
  const decoder = new StringDecoder('latin1');
  const buffer = Buffer.from(response.data);
  response.data = decoder.write(buffer);
  return response;
};

export const apiReport = {
  getReports,
  getReportMetadata,
  getReportFilter,
  getTableResult,
  getExportClassUsersResult,
};
